/**
 * Pair History hook — Pro-only CRUD over /api/pairings.
 *
 * Free users get an upgrade prompt at the page level; this hook
 * still returns an empty list + loading=false so the page renders.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { usePro } from "./pro";

export function usePairings() {
  const { isPro, checking } = usePro();
  const [pairings, setPairings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    if (checking) return;
    if (!isPro) {
      setPairings([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/pairings");
      setPairings(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load pairings");
      setPairings([]);
    } finally {
      setLoading(false);
    }
  }, [isPro, checking]);

  useEffect(() => {
    const t = setTimeout(() => { refresh(); }, 0);
    return () => clearTimeout(t);
  }, [refresh]);

  const create = useCallback(async (payload) => {
    const { data } = await api.post("/pairings", payload);
    setPairings((p) => [data, ...p]);
    return data;
  }, []);

  const update = useCallback(async (id, payload) => {
    const { data } = await api.put(`/pairings/${id}`, payload);
    setPairings((p) => p.map((x) => (x.id === id ? data : x)));
    return data;
  }, []);

  const remove = useCallback(async (id) => {
    await api.delete(`/pairings/${id}`);
    setPairings((p) => p.filter((x) => x.id !== id));
  }, []);

  return { pairings, loading, error, isPro, refresh, create, update, remove };
}
